import React, { useMemo } from 'react';
import { observer } from 'mobx-react-lite';
import {
  IconButton,
  Badge,
  Popover,
  Box,
  Typography,
  List,
  ListItem,
  ListItemText,
  Divider,
  Button,
  Tooltip,
} from '@mui/material';
import { CheckCircleIcon, CloseIcon, ErrorIcon, InfoIcon, NotificationsIcon, NotificationsNoneIcon, WarningIcon } from "../../icons";
import { useRootStore } from '../../store/contexts';
import { AppNotification } from '../../store/NotificationStore';
import { displayedNotification } from '../notifications/notificationText';
import { notificationLinkTarget, NotificationLinkTarget } from '../notifications/notificationLink';

const getRelativeTime = (date: Date, language: string): string => {
  const now = new Date();
  const diffMs = now.getTime() - new Date(date).getTime();
  const diffSecs = Math.floor(diffMs / 1000);
  const diffMins = Math.floor(diffSecs / 60);
  const diffHours = Math.floor(diffMins / 60);
  const diffDays = Math.floor(diffHours / 24);

  const labels: Record<string, Record<string, string>> = {
    justNow: { es: 'ahora', en: 'just now', pt: 'agora' },
    minutesAgo: { es: 'hace {n} min', en: '{n} min ago', pt: 'há {n} min' },
    hoursAgo: { es: 'hace {n} h', en: '{n} h ago', pt: 'há {n} h' },
    yesterday: { es: 'ayer', en: 'yesterday', pt: 'ontem' },
    daysAgo: { es: 'hace {n} días', en: '{n} days ago', pt: 'há {n} dias' },
  };

  if (diffSecs < 60) {
    return labels.justNow[language] || labels.justNow.en;
  } else if (diffMins < 60) {
    return (labels.minutesAgo[language] || labels.minutesAgo.en).replace('{n}', String(diffMins));
  } else if (diffHours < 24) {
    return (labels.hoursAgo[language] || labels.hoursAgo.en).replace('{n}', String(diffHours));
  } else if (diffDays === 1) {
    return labels.yesterday[language] || labels.yesterday.en;
  } else {
    return (labels.daysAgo[language] || labels.daysAgo.en).replace('{n}', String(diffDays));
  }
};

const getTypeIcon = (type: AppNotification['type']) => {
  switch (type) {
    case 'success':
      return <CheckCircleIcon fontSize="small" color="success" />;
    case 'warning':
      return <WarningIcon fontSize="small" color="warning" />;
    case 'error':
      return <ErrorIcon fontSize="small" color="error" />;
    case 'info':
    default:
      return <InfoIcon fontSize="small" color="info" />;
  }
};

export const NotificationBell: React.FC = observer(() => {
  const rootStore = useRootStore();
  const { notificationStore, langStore } = rootStore;
  const [anchorEl, setAnchorEl] = React.useState<HTMLButtonElement | null>(null);

  const unreadCount = notificationStore.unreadCount;
  const notifications = notificationStore.sortedNotifications;
  const open = Boolean(anchorEl);
  const [expandedId, setExpandedId] = React.useState<string | null>(null);

  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    notificationStore.clearRead();
    setAnchorEl(null);
  };

  const handleNotificationClick = (notification: AppNotification) => {
    notificationStore.markAsRead(notification.id);

    // Click SIEMPRE expande/colapsa el texto completo. Antes, si había
    // actionUrl el click navegaba — justo las notificaciones accionables
    // (las que más texto tienen) nunca podían leerse enteras. La navegación
    // ahora va por el botón de acción explícito.
    setExpandedId(prev => prev === notification.id ? null : notification.id);
  };

  const handleAction = (e: React.MouseEvent, notification: AppNotification, target: NotificationLinkTarget) => {
    e.stopPropagation();
    notificationStore.markAsRead(notification.id);
    setAnchorEl(null);
    // A dónde lleva el enlace lo decide notificationLinkTarget (el botón "Ver"
    // solo aparece si lleva a algún lado): uno de la app va por el router, sin
    // recargar la página, y uno externo por window.location.
    if (target.kind === 'app') {
      rootStore.router.goTo(target.path);
    } else {
      window.location.href = target.href;
    }
  };

  const handleDelete = (e: React.MouseEvent, id: string) => {
    e.stopPropagation();
    notificationStore.deleteNotification(id);
  };

  const handleMarkAllAsRead = () => {
    notificationStore.markAllAsRead();
  };

  const badgeContent = useMemo(() => {
    if (unreadCount === 0) return 0;
    if (unreadCount > 99) return '99+';
    return unreadCount;
  }, [unreadCount]);

  const ariaLabel = useMemo(() => {
    if (unreadCount === 0) {
      return langStore.notificationsTitle;
    }
    return `${unreadCount} ${langStore.notificationsUnread}`;
  }, [unreadCount, langStore.notificationsTitle, langStore.notificationsUnread]);

  return (
    <>
      <Tooltip title={ariaLabel} placement="bottom">
        <IconButton
          size="large"
          onClick={handleClick}
          aria-label={ariaLabel}
          aria-controls={open ? 'notifications-popover' : undefined}
          aria-haspopup="true"
          aria-expanded={open ? 'true' : undefined}
        >
          <Badge
            badgeContent={badgeContent}
            color="error"
            invisible={unreadCount === 0}
            sx={{
              '& .MuiBadge-badge': {
                fontSize: '0.625rem',
                minWidth: '18px',
                height: '18px',
              },
            }}
          >
            {unreadCount > 0 ? (
              <NotificationsIcon sx={{ color: 'text.secondary' }} />
            ) : (
              <NotificationsNoneIcon sx={{ color: 'text.secondary' }} />
            )}
          </Badge>
        </IconButton>
      </Tooltip>

      <Popover
        id="notifications-popover"
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
        PaperProps={{
          sx: {
            width: 360,
            maxHeight: 480,
          },
        }}
        role="dialog"
        aria-label={langStore.notificationsTitle}
      >
        <Box
          sx={{
            px: 2,
            py: 1.5,
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            borderBottom: 1,
            borderColor: 'divider',
          }}
        >
          <Typography variant="subtitle1" fontWeight="bold">
            {langStore.notificationsTitle}
          </Typography>
          {unreadCount > 0 && (
            <Button size="small" onClick={handleMarkAllAsRead}>
              {langStore.markAllAsRead}
            </Button>
          )}
        </Box>

        {notifications.length === 0 ? (
          <Box
            sx={{
              py: 6,
              px: 2,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              color: 'text.secondary',
            }}
          >
            <NotificationsNoneIcon sx={{ fontSize: 48, mb: 1, opacity: 0.5 }} />
            <Typography variant="body2">{langStore.noNotifications}</Typography>
          </Box>
        ) : (
          <List
            sx={{
              maxHeight: 400,
              overflow: 'auto',
              py: 0,
            }}
          >
            {notifications.map((notification, index) => {
              // El texto y el destino del enlace se deciden una vez por aviso y por dibujo.
              const shown = displayedNotification(notification, langStore);
              const linkTarget = notificationLinkTarget(notification.data?.actionUrl);
              return (
                <React.Fragment key={notification.id}>
                  <ListItem
                    onClick={() => handleNotificationClick(notification)}
                    sx={{
                      cursor: 'pointer',
                      borderLeft: notification.read ? 'none' : '4px solid',
                      borderLeftColor: 'primary.main',
                      backgroundColor: notification.read ? 'transparent' : 'action.hover',
                      '&:hover': {
                        backgroundColor: 'action.selected',
                      },
                      '&:hover .delete-btn': {
                        opacity: 1,
                      },
                      py: 1.5,
                      pr: 1,
                    }}
                  >
                    <Box sx={{ mr: 1.5, display: 'flex', alignItems: 'flex-start', pt: 0.5 }}>
                      {getTypeIcon(notification.type)}
                    </Box>
                    <ListItemText
                      primary={
                        <Typography
                          variant="body2"
                          fontWeight={notification.read ? 'normal' : 'bold'}
                          noWrap={expandedId !== notification.id}
                        >
                          {shown.title}
                        </Typography>
                      }
                      secondary={
                        <Box>
                          <Typography
                            variant="caption"
                            color="text.secondary"
                            sx={expandedId === notification.id ? {
                              whiteSpace: 'pre-wrap',
                              wordBreak: 'break-word',
                            } : {
                              display: '-webkit-box',
                              WebkitLineClamp: 2,
                              WebkitBoxOrient: 'vertical',
                              overflow: 'hidden',
                            }}
                          >
                            {shown.message}
                          </Typography>
                          <Box sx={{ mt: 0.5, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                            <Typography variant="caption" color="text.disabled">
                              {getRelativeTime(notification.timestamp, langStore.language)}
                            </Typography>
                            {linkTarget && (
                              <Button
                                size="small"
                                sx={{ py: 0, minWidth: 0, fontSize: '0.7rem' }}
                                onClick={(e) => handleAction(e, notification, linkTarget)}
                              >
                                {langStore.viewCommand}
                              </Button>
                            )}
                          </Box>
                        </Box>
                      }
                      // El secondary lleva un Box (un div): que ListItemText no lo meta en un <p>.
                      secondaryTypographyProps={{ component: 'div' }}
                    />
                    <IconButton
                      size="small"
                      className="delete-btn"
                      onClick={(e) => handleDelete(e, notification.id)}
                      sx={{
                        opacity: 0,
                        transition: 'opacity 0.2s',
                        ml: 1,
                      }}
                      aria-label={langStore.deleteCommand}
                    >
                      <CloseIcon fontSize="small" />
                    </IconButton>
                  </ListItem>
                  {index < notifications.length - 1 && <Divider component="li" />}
                </React.Fragment>
              );
            })}
          </List>
        )}
      </Popover>
    </>
  );
});
